class FormValidator {
  constructor(form, options = {}) {
    if (!(form instanceof HTMLFormElement)) {
      throw new Error('FormValidator expects a form element');
    }

    this.form = form;
    this.options = {
      onSubmit: null,
      messages: {},
      customValidators: {},
      ...options,
    };

    this.fields = Array.from(
      this.form.querySelectorAll('input, select, textarea')
    ).filter(el => !el.disabled);

    this._bindEvents();
  }

  /* -------------------------------------------- */
  /* EVENTS */
  /* -------------------------------------------- */

  _bindEvents() {
    this.form.addEventListener('submit', e => {
      if (!this.validateForm()) {
        e.preventDefault();
        e.stopPropagation();
      } else if (typeof this.options.onSubmit === 'function') {
        e.preventDefault();
        this.options.onSubmit(this.form);
      }
    });

    this.fields.forEach(field => {
      field.addEventListener('input', () => this.validateField(field));
      field.addEventListener('change', () => this.validateField(field));
    });
  }

  /* -------------------------------------------- */
  /* FORM VALIDATION */
  /* -------------------------------------------- */

  validateForm() {
    let isValid = true;

    this.form.classList.add('was-validated');

    this.fields.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    return isValid;
  }

  /* -------------------------------------------- */
  /* FIELD VALIDATION */
  /* -------------------------------------------- */

  validateField(field) {
    const validity = field.validity;
    let message = '';

    // Custom validator (if any)
    const custom = this.options.customValidators[field.name];
    if (custom) {
      const customResult = custom(field.value, field);
      if (customResult !== true) {
        field.setCustomValidity(customResult);
      } else {
        field.setCustomValidity('');
      }
    }

    if (!field.checkValidity()) {
      message = this._getMessage(field, validity);
      this._setInvalid(field, message);
      return false;
    }

    this._setValid(field);
    return true;
  }

  /* -------------------------------------------- */
  /* MESSAGES */
  /* -------------------------------------------- */

  _getMessage(field, validity) {
    const name = field.name;

    if (this.options.messages[name]) {
      return this.options.messages[name];
    }

    if (validity.valueMissing) return 'This field is required.';
    if (validity.typeMismatch) return 'Please enter a valid value.';
    if (validity.tooShort)
      return `Minimum ${field.minLength} characters required.`;
    if (validity.tooLong)
      return `Maximum ${field.maxLength} characters allowed.`;
    if (validity.rangeUnderflow) return `Minimum value is ${field.min}.`;
    if (validity.rangeOverflow) return `Maximum value is ${field.max}.`;
    if (validity.stepMismatch) return 'Please enter a valid step value.';
    if (validity.patternMismatch) return 'Invalid format.';
    if (validity.badInput) return 'Invalid input.';

    return 'Invalid value.';
  }

  /* -------------------------------------------- */
  /* UI HELPERS (BOOTSTRAP 5) */
  /* -------------------------------------------- */

  _setInvalid(field, message) {
    field.classList.remove('is-valid');
    field.classList.add('is-invalid');

    const feedback = this._getFeedbackElement(field);
    if (feedback) feedback.textContent = message;
  }

  _setValid(field) {
    field.classList.remove('is-invalid');
    field.classList.add('is-valid');

    const feedback = this._getFeedbackElement(field);
    if (feedback) feedback.textContent = '';
  }

  _getFeedbackElement(field) {
    if (field.type === 'radio' || field.type === 'checkbox') {
      return field
        .closest('.mb-3, .form-group')
        ?.querySelector('.invalid-feedback');
    }

    return field.nextElementSibling?.classList.contains('invalid-feedback')
      ? field.nextElementSibling
      : null;
  }
}
